/**
 * ProgressGate.jsx — Round 17: the Progress Gate enforcement overlay.
 *
 * Blocks Cortex for a project's Client Manager once that project's active Fixed-Fee phase(s)
 * haven't had Percent Complete Override confirmed since this week's Monday reset. Deliberately
 * separate from TimeGate.jsx — different responsibility model (an Airtable "Client Manager" per
 * project, not a personal policy row) and cadence. See docs/PROGRESS_GATE.md.
 *
 * Each row also shows a fee/hours-implied comparison (2026-08-10) — feeExpended ÷ Contracted Fee
 * from live Supabase time entries, the same Fixed-Fee numerator docs/EBR_FRAMEWORK.md uses — next
 * to the manual override, so the Client Manager can see how far the two have drifted and correct
 * toward alignment over time. Purely informational: it never pre-fills or overrides the input —
 * the number stays a human judgment call on purpose (docs/PROGRESS_GATE.md's "the number stays
 * manual").
 *
 * Mirrors TimeGate.jsx's shape (mount-time wait loop, focus recheck, fail-open on structural
 * non-membership) but ships its own inline % editor — reusing the SAME guarded write
 * (services/phases.js#savePhasePercentComplete) the Billing Run's phase modal uses, including its
 * "reduce below current" confirmation, rather than a naive duplicate write path.
 *
 * Defers to the Time Gate (2026-08, Matt's call): listens for TimeGate.jsx's
 * `time-gate-blocking-changed` event and stays hidden while it's blocking, even if this gate's own
 * conditions are met — time takes priority, and the two full-screen overlays must never stack or
 * race each other. Defaults to deferring until Time Gate's state is actually known (not just
 * "assumed clear"), so this never flashes visible before Time Gate has had its own first check.
 *
 * Mounted once in OutlineView, alongside <TimeGate/>.
 */

import RoleChip from './RoleChip.jsx'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useClerk } from '@clerk/clerk-react'
import { ctx } from '../outline/ctx.js'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { getProgressGateStatus, snoozeProgressGate, pauseProgressGate, confirmPhaseProgress, stillOpenMilestones } from '../services/progressGate.js'
import { openMilestoneDiscussion } from '../services/slack.js'
import { savePhasePercentComplete } from '../services/phases.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { lockScroll } from '../outline/scrollLock.js'
import ProjectBriefPanel from './ProjectBriefPanel.jsx'
import LaunchBoard from './LaunchBoard.jsx'
import FocusBoard from './FocusBoard.jsx'
import { getCrewBoard, crewPicks } from '../services/alderflow.js'

/** Highest percent a phase may read while milestones in it are open (mirrors api/progress-gate.js). */
function phaseCap(phaseName, open) {
  if (!open || !open.length) return 100
  const planSetPhase = /discretionary approvals|construction documents/i.test(String(phaseName || ''))
  const beforeFirstSubmission = open.some(m => /initial agency submission|plan check drawings/i.test(String(m.name || '')))
  return planSetPhase && beforeFirstSubmission ? 80 : 99
}

export default function ProgressGate() {
  const [status, setStatus] = useState(null)     // { gated, phases:[...] } | null
  const [busy, setBusy] = useState('')            // phaseId (or '__snooze__'/'__pause__') being acted on
  const [ready, setReady] = useState(false)
  const [verifyFailed, setVerifyFailed] = useState(false)
  const [drafts, setDrafts] = useState({})        // phaseId -> in-progress input value (string)
  const [snoozeMsg, setSnoozeMsg] = useState('')
  const [timeGateBlocking, setTimeGateBlocking] = useState(null)  // null = unknown yet — defer
  const [triageBlocking, setTriageBlocking] = useState(null)       // TriageGate.jsx (2026-09-09) — same deferral
  // ONE CARD PER PROJECT (Matt, 2026-10-19, the Focus Walk card style): a slider per phase in whole
  // percents, Confirm moves to the next project, Back revisits one already confirmed to revise it.
  // The deck is a snapshot taken when the gate opens, so confirmed projects stay reachable by Back
  // even though the server no longer lists them.
  const [deck, setDeck] = useState(null)          // [{ key, projectName, phases:[...] }]
  const [idx, setIdx] = useState(0)
  const [savedPct, setSavedPct] = useState({})    // phaseId -> pct saved in this session
  const [doneKeys, setDoneKeys] = useState({})    // project key -> true once confirmed
  const [flash, setFlash] = useState('')          // the answer-back line after a confirm
  const [anim, setAnim] = useState('')            // '' | 'out' | 'in'
  // THE WEEKLY PLAN opens on the launch board (Matt, 2026-10-25: "I want to first see the launch board").
  const [boardSeen, setBoardSeen] = useState(false)
  // ...and ends on the week's crews for the tower (docs/ALDERFLOW.md §5c3).
  const [crewStep, setCrewStep] = useState(false)
  // Milestones posted from this plan (they leave a phase's open list at once).
  const [postedNow, setPostedNow] = useState(() => new Set())
  useEffect(() => {
    const on = (e) => { if (e.detail && e.detail.posted && e.detail.msId) { setPostedNow(s => new Set([...s, e.detail.msId])); setLanded(s => new Set([...s, e.detail.msId])) } }
    window.addEventListener('post-ms-modal-closed', on)
    return () => window.removeEventListener('post-ms-modal-closed', on)
  }, [])
  // CHECK AGAIN (Matt, 2026-11-02): talk it through in the milestone's own Slack discussion, then check the
  // card again when they say it's posted; it also checks when you come back to this window. No refresh.
  const [landed, setLanded] = useState(() => new Set())   // posted while this card was open
  const [checkMsg, setCheckMsg] = useState('')
  const [checking, setChecking] = useState(false)
  const watchIds = (() => {
    const c = deck && deck[Math.min(idx, deck.length - 1)]
    if (!c) return ''
    return c.phases.flatMap(p => (p.openMilestones || []).map(m => m.id)).filter(id => !postedNow.has(id)).sort().join(',')
  })()
  const checkAgain = useCallback(async (quiet) => {
    if (!watchIds) return
    const ids = watchIds.split(',')
    if (!quiet) { setChecking(true); setCheckMsg('') }
    try {
      const open = await stillOpenMilestones(ids)
      if (!open) { if (!quiet) setCheckMsg('Could not check just now. Try again.'); return }
      const gone = ids.filter(id => !open.includes(id))
      if (gone.length) { setPostedNow(s => new Set([...s, ...gone])); setLanded(s => new Set([...s, ...gone])); setCheckMsg('') }
      else if (!quiet) setCheckMsg(open.length === 1 ? 'Still open.' : 'All ' + open.length + ' still open.')
    } catch { if (!quiet) setCheckMsg('Could not check just now. Try again.') }
    finally { if (!quiet) setChecking(false) }
  }, [watchIds])
  useEffect(() => { setCheckMsg('') }, [watchIds])
  useEffect(() => {
    if (!watchIds || !boardSeen) return
    const onFocus = () => checkAgain(true)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [watchIds, boardSeen, checkAgain])
  const pollRef = useRef(null)
  const { signOut } = useClerk()

  useEffect(() => {
    function onTimeGate(e) { setTimeGateBlocking(!!(e.detail && e.detail.blocking)) }
    function onTriage(e) { setTriageBlocking(!!(e.detail && e.detail.blocking)) }
    window.addEventListener('time-gate-blocking-changed', onTimeGate)
    window.addEventListener('triage-gate-blocking-changed', onTriage)
    return () => { window.removeEventListener('time-gate-blocking-changed', onTimeGate); window.removeEventListener('triage-gate-blocking-changed', onTriage) }
  }, [])

  const memberId = ctx.currentMember && ctx.currentMember.id
  const phases = (status && Array.isArray(status.phases)) ? status.phases : []
  const gatedNow = !!(status && status.gated && phases.length)
  // Same peek/split-iframe bypass as TimeGate.jsx — a fresh copy of the app loaded in an embedded
  // pane must never show its own full-screen lockout over what's meant to be a live window into
  // the real outline (Matt, 2026-08-29).
  const blocking = !isEmbedded() && (gatedNow || verifyFailed) && timeGateBlocking === false && triageBlocking === false

  const recheck = useCallback(async () => {
    const mid = ctx.currentMember && ctx.currentMember.id
    if (!isSeatLoaded() || !mid) return
    try {
      const s = await getProgressGateStatus(mid)
      if (!s || typeof s.gated !== 'boolean') throw new Error('bad-response')
      setStatus(s); setVerifyFailed(false)
    } catch {
      // Unlike the Time Gate, there's no cached "safe reason" fallback here — a Progress Gate
      // failure to verify simply doesn't block (fail-open), since this gate is additive/optional
      // and never the sole thing standing between a person and their own work.
      setStatus({ gated: false }); setVerifyFailed(false)
    }
  }, [])

  useEffect(() => {
    if (ready) return
    let tries = 0
    pollRef.current = setInterval(() => {
      tries++
      if (isSeatLoaded() && ctx.currentMember && ctx.currentMember.id) {
        clearInterval(pollRef.current); pollRef.current = null
        setReady(true); recheck()
      } else if (tries > 40) {
        clearInterval(pollRef.current); pollRef.current = null
      }
    }, 400)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [ready, recheck])

  useEffect(() => {
    const onFocus = () => recheck()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [recheck])

  useEffect(() => {
    if (!blocking) return
    return lockScroll()   // shared counter: the page scrolls again once every lockout has closed
  }, [blocking])

  // Snapshot the deck the first time phases arrive (see `deck` above).
  useEffect(() => {
    if (deck || !phases.length) return
    const gs = []
    for (const p of phases) {
      const last = gs[gs.length - 1]
      if (last && last.key === (p.projectUid || p.projectName)) last.phases.push(p)
      else gs.push({ key: p.projectUid || p.projectName, projectName: p.projectName, phases: [p] })
    }
    setDeck(gs)
  }, [deck, phases])

  const isTower = isSeatLoaded() && (hasModule('workload') || hasModule('admin'))
  if (crewStep) return (
    <div style={S.scrim}>
      <style>{CSS}</style>
      <div className="aj-pg">
        <div className="aj-pg-top"><span className="aj-pg-t">Weekly plan · this week’s crews</span></div>
        <div className="aj-pg-card"><CrewWeek onDone={() => { setCrewStep(false); setBoardSeen(false); recheck() }} /></div>
      </div>
    </div>
  )
  if (!blocking) return null

  const isAdmin = isSeatLoaded() && hasModule('admin')

  // Phases arrive pre-grouped-and-ordered from the server (project name, then the firm's own phase
  // sequence). One card per project.
  const groups = []
  for (const p of phases) {
    const last = groups[groups.length - 1]
    if (last && last.key === (p.projectUid || p.projectName)) last.phases.push(p)
    else groups.push({ key: p.projectUid || p.projectName, projectName: p.projectName, phases: [p] })
  }
  const cards = deck || groups
  const at = Math.min(idx, Math.max(0, cards.length - 1))
  const g = cards[at]
  if (!g) return null

  const base = p => (savedPct[p.phaseId] != null ? savedPct[p.phaseId] : p.pctComplete)
  const valueOf = p => (drafts[p.phaseId] != null ? Number(drafts[p.phaseId]) : base(p))
  const changed = g.phases.filter(p => valueOf(p) !== base(p)).length
  // NO PHASE AT 100% WITH OPEN MILESTONES (Matt, 2026-10-26). The slider stops at 99% while any are open, and
  // a phase already at 100% with open milestones blocks Confirm until they post or it comes down.
  const openOf = p => (p.openMilestones || []).filter(m => !postedNow.has(m.id))
  // THE PLAN-SET CAP (Matt, 2026-10-26): DA and CD stop at 80% until the first submission posts, then may run
  // to 99% through corrections; mirrors api/progress-gate.js#phaseCap.
  // …but never below what has already been INVOICED (Matt, 2026-10-28, Gamber DA billed to 100% with its
  // last revision milestone still open): lowering it would claw back a bill. The server checks the same.
  const capOf = p => Math.max(p.billedPercent || 0, phaseCap(p.phaseName, openOf(p)))
  const blocked100 = g.phases.filter(p => valueOf(p) > capOf(p))
  const gBusy = busy === 'proj:' + g.key

  function go(next) {
    setAnim('out')
    setTimeout(() => { setIdx(next); setFlash(''); setAnim('in'); setTimeout(() => setAnim(''), 30) }, 220)
  }

  // Confirm the card: write the phases that moved (same guarded write the Billing Run uses), and
  // confirm EVERY phase, changed or not. Airtable's "Percent Complete Updated" only ticks on a real
  // change, so an unchanged phase would otherwise keep coming back.
  async function confirmCard() {
    const parsed = g.phases.map(p => ({ p, from: base(p), pct: Math.round(valueOf(p)) }))
    const reduced = parsed.filter(x => x.pct < x.p.pctComplete)
    if (reduced.length) {
      const lines = reduced.map(x => `${x.p.phaseName}: ${x.p.pctComplete}% to ${x.pct}%`).join('\n')
      const ok = await confirmDialog({
        title: 'Reduce phase progress?',
        message: `${g.projectName}\n${lines}\n\nThis may set the billing basis below what has already been invoiced. Confirm the reduction is intentional and accurate.`,
        confirmLabel: 'I have reviewed, proceed',
        danger: true,
      })
      if (!ok) return
    }
    setBusy('proj:' + g.key)
    try {
      for (const { p, from, pct } of parsed) {
        if (pct !== from) await savePhasePercentComplete(p.phaseId, pct / 100, memberId)
      }
      await Promise.all(parsed.map(({ p, pct }) => confirmPhaseProgress(p.phaseId, pct)))
      setSavedPct(m => { const n = { ...m }; parsed.forEach(({ p, pct }) => { n[p.phaseId] = pct }); return n })
      setDrafts(d => { const n = { ...d }; parsed.forEach(({ p }) => { delete n[p.phaseId] }); return n })
      const nowDone = { ...doneKeys, [g.key]: true }
      setDoneKeys(nowDone)
      const moved = parsed.filter(x => x.pct !== x.from).length
      setFlash(moved ? `Saved. ${moved} phase${moved === 1 ? '' : 's'} updated.` : 'Confirmed as is.')
      const nextOpen = cards.findIndex((c, i) => i > at && !nowDone[c.key])
      const anyOpen = cards.findIndex(c => !nowDone[c.key])
      setTimeout(() => {
        if (nextOpen >= 0) go(nextOpen)
        else if (anyOpen >= 0) go(anyOpen)
        else { setDeck(null); setIdx(0); setDoneKeys({}); setSavedPct({}); if (isTower) setCrewStep(true); else recheck() }
      }, 650)
    } catch (e) {
      await alertDialog({ title: 'Could not save', message: (e && e.message) || 'Something went wrong. Nothing was skipped; try again.' })
    } finally { setBusy('') }
  }

  async function doSnooze(hours) {
    setBusy('__snooze__'); setSnoozeMsg('')
    try {
      const r = await snoozeProgressGate(memberId, hours)
      if (r && r.ok === false) setSnoozeMsg(r.error || 'Could not snooze.')
      else recheck()
    } finally { setBusy('') }
  }
  async function doPause(hours) {
    setBusy('__pause__')
    try { await pauseProgressGate(hours) } finally { setBusy(''); recheck() }
  }

  async function discuss(m) {
    const link = (typeof ctx.entityShareUrl === 'function') ? ctx.entityShareUrl('milestone', m.id) : ''
    const r = await openMilestoneDiscussion({ msId: m.id, msName: m.name, projectId: g.phases[0] && g.phases[0].projectUid, projectName: g.projectName,
      startedBy: (ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Member Name']) || '', deepLink: link })
    if (!r.ok) await alertDialog({ title: 'Could not open Slack', message: r.error })
  }
  // One row per open milestone holding the cap: post it yourself, or talk it through in its Slack discussion.
  function openRows(p) {
    const open = openOf(p)
    const done = (p.openMilestones || []).filter(m => landed.has(m.id))
    if (!open.length && !done.length) return null
    return (
      <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
        {done.map(m => (
          <div key={m.id} style={{ fontSize: 12.5, color: '#1f7a4d', fontWeight: 600 }}>✓ {m.name} posted{capOf(p) >= 100 ? '. This phase can go to 100% now.' : '.'}</div>
        ))}
        {open.map(m => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <span style={{ flex: 1, minWidth: 0, color: 'var(--aj-text-mid)' }}>{m.name}</span>
            <button type="button" className="aj-pg-ico" title="Discuss in Slack" aria-label={'Discuss ' + m.name + ' in Slack'} onClick={() => discuss(m)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a7.5 7.5 0 0 1-11.2 6.5L4 20l1.5-4.6A7.5 7.5 0 1 1 20 12Z" /></svg>
            </button>
            <button type="button" className="aj-pg-ico" title="Post it" aria-label={'Post ' + m.name} onClick={() => window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: m.id, zIndex: 1000006, instruction: 'Post ' + m.name + ' dated the day it really finished.' } }))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8.5" /><path d="m8.3 12.3 2.5 2.5 4.9-5.1" /></svg>
            </button>
          </div>
        ))}
      </div>
    )
  }

  const remaining = cards.filter(c => !doneKeys[c.key]).length
  return (
    <div style={S.scrim}>
      <style>{CSS}</style>
      <div className="aj-pg" style={boardSeen ? undefined : { maxWidth: 1080 }}>
        <div className="aj-pg-top">
          <span className="aj-pg-t">{boardSeen ? `Weekly plan · ${at + 1} of ${cards.length}` : 'Weekly plan'}</span>
          <span className="aj-pg-dots">
            {cards.map((c, i) => <i key={c.key} className={i === at ? 'now' : doneKeys[c.key] ? 'past' : ''} />)}
          </span>
        </div>
        {!boardSeen ? <div className="aj-pg-card"><LaunchBoard onStart={() => setBoardSeen(true)} startLabel={`Start the plan · ${cards.length} project${cards.length === 1 ? '' : 's'}`} /></div> : <div className={'aj-pg-card' + (anim ? ' ' + anim : '')}>
          <RoleChip role="Client Manager" what="Weekly plan for a project you manage" />
          <div className="aj-pg-kind">{doneKeys[g.key] ? 'Confirmed · revising' : 'Where does each phase stand?'}</div>
          <h1 className="aj-pg-h1">{g.projectName}</h1>
          {/* The project's whole picture where you are already thinking about it (Matt, 2026-10-25). */}
          {g.phases[0] && g.phases[0].projectUid && <ProjectBriefPanel projectId={g.phases[0].projectUid} projectName={g.projectName} />}
          <p className="aj-pg-sub">Slide only what moved. The AR team bills from these numbers.</p>
          <div className="aj-pg-phases">
            {g.phases.map(p => {
              const v = valueOf(p)
              const moved = v !== base(p)
              return (
                <div key={p.phaseId} className="aj-pg-ph">
                  <div className="aj-pg-ph-head">
                    <span className="aj-pg-ph-name">{String(p.phaseName || '').replace(/\s*Phase$/, '')}</span>
                    {p.openInCore && (
                      <a href={p.openInCore} target="_blank" rel="noopener noreferrer" title="Open this phase in Core" className="aj-pg-core">Core ↗</a>
                    )}
                    <span className={'aj-pg-pct' + (moved ? ' moved' : '')}>{v}%</span>
                  </div>
                  <div className="aj-pg-track">
                    {/* Where the hours logged so far put it: a small mark under the slider, never on
                        it, and never moving it. */}
                    {p.impliedPercent != null && p.impliedPercent <= 100 && (
                      <span className="aj-pg-implied" style={{ left: `calc(12px + (100% - 24px) * ${Math.max(0, Math.min(100, p.impliedPercent)) / 100})` }}
                        title={`Hours logged so far imply about ${p.impliedPercent}% ($${(p.feeExpended || 0).toLocaleString()} of $${(p.contractedFee || 0).toLocaleString()} contracted)`} />
                    )}
                    {/* The slider is always a true 0-100 scale, so the dot and the fill agree (Matt, 2026-10-28,
                        Luisi CD: with max set to the 80% cap the dot sat at the end while the fill stopped at
                        80%). A cap stops the dot instead, and a tick marks where. */}
                    {capOf(p) < 100 && (
                      <span title={`Stops at ${capOf(p)}% for now`} style={{ position: 'absolute', top: '50%', height: 14, width: 2, marginTop: -7, borderRadius: 1, background: 'var(--aj-text-muted)', opacity: 0.6, pointerEvents: 'none',
                        left: `calc(12px + (100% - 24px) * ${capOf(p) / 100} - 1px)` }} />
                    )}
                    {/* WHERE IT STARTED (Matt, 2026-11-02): once the dot moves, a hollow ring stays where it was
                        when the card came up, with its percent; clicking it puts the dot back. */}
                    {moved && (
                      <button type="button" className="aj-pg-was" title={'Put it back to ' + base(p) + '%'} aria-label={'Put ' + p.phaseName + ' back to ' + base(p) + '%'}
                        style={{ left: `calc(12px + (100% - 24px) * ${base(p) / 100})` }}
                        onClick={() => setDrafts(d => { const n = { ...d }; delete n[p.phaseId]; return n })}>
                        <span className="aj-pg-was-lbl">{base(p)}%</span>
                      </button>
                    )}
                    <input type="range" min="0" max="100" step="1" value={v} disabled={gBusy}
                      aria-label={p.phaseName + ' percent complete'}
                      style={{ '--v': `calc(12px + (100% - 24px) * ${v / 100})` }}
                      // Never past the cap, except back up to where it was saved (a phase already over the
                      // cap can come down and return, never go higher).
                      onChange={e => setDrafts(d => ({ ...d, [p.phaseId]: String(Math.min(Math.max(capOf(p), base(p)), Math.round(Number(e.target.value)))) }))} />
                  </div>
                  {v > capOf(p) && (
                    <div style={S.warn100}>
                      <div style={S.warn100Head}>⚠ Marked {v}% complete, but {openOf(p).length} milestone{openOf(p).length === 1 ? ' is' : 's are'} still open</div>
                      <div style={{ fontSize: 13, marginBottom: 6 }}>{capOf(p) === 80
                        ? 'Discretionary Approvals and Construction Documents stop at 80% until the plan set has gone in for its first submission. Post the submission if it has gone in, with its real date, or slide this phase down to 80%.'
                        : 'A phase can only be 100% when everything in it has posted. Post what is done, with the date it really finished, or slide this phase down.'}</div>
                      {openOf(p).map(m => (
                        <div key={m.id} style={S.warn100Row}>
                          <span>{m.name}{m.due ? <span style={{ opacity: 0.75 }}> · due {new Date(m.due + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span> : null}</span>
                          <button type="button" style={S.warn100Btn} onClick={() => window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: m.id, zIndex: 1000006, instruction: `${p.phaseName} reads 100%. Post ${m.name} dated the day it really finished.` } }))}>Post it…</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {v <= capOf(p) && openOf(p).length > 0 && (p.billedPercent || 0) >= v && v > phaseCap(p.phaseName, openOf(p)) && (
                    <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 4 }}>Already billed to {p.billedPercent}%, so it stays here while {openOf(p).length === 1 ? 'its last milestone posts' : `its ${openOf(p).length} open milestones post`}.</div>
                  )}
                  {/* WHY IT STOPS (Matt, 2026-10-28: "should the bar tell us why it's stopping us at 80%?"):
                      whenever a cap is on, one line names what lifts it. Not when the billed floor is the
                      reason (its own line above) or the over-cap warning is showing. */}
                  {v <= capOf(p) && capOf(p) < 100 && !((p.billedPercent || 0) >= v && v > phaseCap(p.phaseName, openOf(p))) && (() => {
                    const open = openOf(p)
                    const first = open.find(m => /initial agency submission|plan check drawings/i.test(String(m.name || '')))
                    const text = capOf(p) === 80 && first
                      ? `Stops at 80% until ${first.name} posts: the plan set's first submission.`
                      : `Stops at ${capOf(p)}% until ${open.length === 1 ? open[0].name + ' posts' : `its ${open.length} open milestones post`}.`
                    return <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 4 }}>{text}</div>
                  })()}
                  {v <= capOf(p) && openRows(p)}
                  {/* Over its fee: the lead, not a footnote (Matt, 2026-10-19 on Hooper DA at 607%). */}
                  {p.impliedPercent > 100 && p.contractedFee > 0 && (
                    <div className="aj-pg-over">
                      Logged time is {p.impliedPercent >= 200 ? (Math.round(p.impliedPercent / 10) / 10) + '×' : p.impliedPercent + '% of'} this phase{'’'}s fee: ${Math.round(p.feeExpended || 0).toLocaleString()} against ${Math.round(p.contractedFee || 0).toLocaleString()}.
                    </div>
                  )}
                  <div className="aj-pg-ph-meta">
                    {moved ? `was ${base(p)}%` : (p.daysStale != null ? `last updated ${p.daysStale} day${p.daysStale === 1 ? '' : 's'} ago` : 'never updated')}
                    {p.impliedPercent != null && p.impliedPercent <= 100 && <span> · <span className="aj-pg-key" /> logged hours put it at about {p.impliedPercent}%</span>}
                    {moved && <button className="aj-pg-undo" onClick={() => setDrafts(d => { const n = { ...d }; delete n[p.phaseId]; return n })}>Undo</button>}
                  </div>
                </div>
              )
            })}
          </div>
          {flash
            ? <div className="aj-pg-done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>{flash}</div>
            : (
              <div className="aj-pg-row">
                <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {at > 0 && <button className="aj-pg-back" onClick={() => go(at - 1)} disabled={gBusy}>← Back</button>}
                  {watchIds && (
                    <button className="aj-pg-back" onClick={() => checkAgain(false)} disabled={checking} title="Look again at whether this card's open milestones have posted">
                      {checking ? 'Checking…' : '↻ Check again'}{checkMsg && !checking ? <span style={{ fontWeight: 400, opacity: 0.75 }}> · {checkMsg}</span> : null}
                    </button>
                  )}
                </span>
                <button className="aj-pg-go" onClick={confirmCard} disabled={gBusy || blocked100.length > 0}>
                  {blocked100.length ? 'Fix the 100% phase first' : gBusy ? 'Saving…' : changed ? `Confirm · ${changed} changed` : 'Confirm as is'}
                </button>
              </div>
            )}
        </div>}
        <div className="aj-pg-left">{remaining === 0 ? 'All confirmed.' : `${remaining} project${remaining === 1 ? '' : 's'} left`}</div>
        {snoozeMsg && <div style={S.snoozeMsg}>{snoozeMsg}</div>}
        <div style={S.footer}>
          <button style={S.linkBtn} onClick={() => signOut()}>Sign out</button>
          <button style={S.linkBtn} onClick={() => doSnooze(24)} disabled={busy === '__snooze__'}>
            {busy === '__snooze__' ? '…' : 'Snooze 1 day'}
          </button>
          {isAdmin && (
            <button style={S.linkBtn} onClick={() => doPause(24)} disabled={busy === '__pause__'}>
              {busy === '__pause__' ? 'Pausing…' : 'Pause for the rest of today (admin)'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// THIS WEEK'S CREWS (docs/ALDERFLOW.md §5c3): the plan's last step, for the tower. The same board as the
// daily where-the-work-goes card: projects with ready work, losing their buffer first, and the people on no
// milestone; picks launch through the app's own checks and refusals are said plainly.
function CrewWeek({ onDone }) {
  const [board, setBoard] = useState(null)
  const [lines, setLines] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { let alive = true; getCrewBoard().then(b => { if (alive) setBoard(b || { free: [], projects: [] }) }).catch(() => { if (alive) setBoard({ free: [], projects: [] }) }); return () => { alive = false } }, [])
  if (board === null) return <div className="aj-pg-sub">Reading the board…</div>
  if (lines) return (
    <div>
      <div className="aj-pg-kind">This week’s crews</div>
      <h1 className="aj-pg-h1">The plan is set.</h1>
      <div style={{ display: 'grid', gap: 4, margin: '8px 0 14px', fontSize: 13.5 }}>
        {lines.length ? lines.map((l, i) => <div key={i} style={{ color: l.ok ? '#1f7a4d' : '#c0392b' }}>{l.t}</div>) : <div className="aj-pg-sub">Nobody new was crewed.</div>}
      </div>
      <div className="aj-pg-row"><span /><button className="aj-pg-go" onClick={onDone}>Done</button></div>
    </div>
  )
  const n = board.free.length
  return (
    <div>
      <RoleChip role="Operations Director" what="Crew the week" />
      <div className="aj-pg-kind">This week’s crews</div>
      <h1 className="aj-pg-h1">{n ? `${n === 1 ? String(board.free[0].name || '').split(' ')[0] + ' isn’t' : n + ' people aren’t'} on any milestone` : 'Everyone has a plane'}</h1>
      <p className="aj-pg-sub">{n ? 'Pick a project, then its milestone, then who flies it. Projects losing their buffer come first.' : 'Nobody is free to crew this week.'}</p>
      {n > 0 && board.projects.length > 0
        ? <FocusBoard board={board} taken={new Set()} onSkip={onDone}
            onCommit={async (picked) => { if (busy) return; setBusy(true); try { setLines(await crewPicks(picked)) } finally { setBusy(false) } }} />
        : <div className="aj-pg-row"><span /><button className="aj-pg-go" onClick={onDone}>Done</button></div>}
    </div>
  )
}

// The Focus Walk card language (docs/alderflow-prototypes/focus-walk.html): one card, one message,
// step dots, a spring slide between cards.
const CSS = `
.aj-pg{--ink:#1d1d1f;--ink2:#6e6e73;--ink3:#a1a1a6;--track:#ececf0;--card:#fff;--bg:#f5f5f7;--open:#f28c28;--good:#1f9d55;--spring:cubic-bezier(.22,1,.36,1);
  width:100%;max-width:600px;margin:auto;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Inter","DM Sans",system-ui,sans-serif;-webkit-font-smoothing:antialiased}
.aj-pg-top{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 4px 14px}
.aj-pg-t{font-size:14px;font-weight:600;color:rgba(255,255,255,.85)}
.aj-pg-dots{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.aj-pg-dots i{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.28);transition:background .3s,width .4s var(--spring)}
.aj-pg-dots i.past{background:rgba(255,255,255,.6)}
.aj-pg-dots i.now{background:#fff;width:20px;border-radius:4px}
.aj-pg-card{background:var(--card);color:var(--ink);border-radius:24px;padding:30px 28px 26px;box-shadow:0 20px 50px rgba(4,21,24,.28);transition:opacity .22s var(--spring),transform .4s var(--spring)}
.aj-pg-card.out{opacity:0;transform:translateX(-36px) scale(.985)}
.aj-pg-card.in{opacity:0;transform:translateX(36px) scale(.985)}
.aj-pg-kind{font-size:13px;font-weight:600;color:#c4610b}
.aj-pg-h1{font-size:clamp(24px,4.6vw,32px);line-height:1.12;font-weight:700;letter-spacing:-.03em;margin:8px 0 8px;text-wrap:balance}
.aj-pg-sub{font-size:15px;line-height:1.5;color:var(--ink2);margin:0 0 20px}
.aj-pg-phases{display:grid;gap:12px;margin:0 0 22px}
.aj-pg-ph{background:var(--bg);border-radius:16px;padding:14px 16px 12px}
.aj-pg-ph-head{display:flex;align-items:baseline;gap:10px}
.aj-pg-ph-name{flex:1;min-width:0;font-size:15px;font-weight:600;letter-spacing:-.01em}
.aj-pg-core{font-size:12px;color:var(--ink3);text-decoration:none}
.aj-pg-core:hover{color:var(--ink)}
.aj-pg-pct{font-size:24px;font-weight:700;letter-spacing:-.02em;font-variant-numeric:tabular-nums;min-width:64px;text-align:right}
.aj-pg-pct.moved{color:#c4610b}
.aj-pg-track{position:relative;margin:8px 0 14px;height:28px;display:flex;align-items:center}
.aj-pg-track input[type=range]{-webkit-appearance:none;appearance:none;width:100%;height:6px;border-radius:3px;margin:0;cursor:pointer;
  background:linear-gradient(to right,var(--ink) 0,var(--ink) var(--v),var(--track) var(--v),var(--track) 100%)}
.aj-pg-track input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:24px;height:24px;border-radius:50%;background:#fff;border:0;box-shadow:0 1px 4px rgba(0,0,0,.25),0 0 0 .5px rgba(0,0,0,.08);transition:transform .15s var(--spring)}
.aj-pg-track input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.1)}
.aj-pg-track input[type=range]::-moz-range-thumb{width:24px;height:24px;border-radius:50%;background:#fff;border:0;box-shadow:0 1px 4px rgba(0,0,0,.25)}
.aj-pg-track input[type=range]:focus-visible{outline:2px solid var(--open);outline-offset:6px}
.aj-pg-track input[type=range]{position:relative;z-index:1}
.aj-pg-was{position:absolute;top:50%;width:14px;height:14px;margin:-7px 0 0 -7px;padding:0;border-radius:50%;border:2px solid var(--ink3);background:var(--card);z-index:2;cursor:pointer;transition:border-color .15s,transform .15s var(--spring)}
.aj-pg-was:hover{border-color:var(--ink);transform:scale(1.15)}
.aj-pg-was-lbl{position:absolute;bottom:15px;left:50%;transform:translateX(-50%);font-size:10.5px;font-weight:600;color:var(--ink2);white-space:nowrap;font-variant-numeric:tabular-nums;pointer-events:none}
.aj-pg-implied{position:absolute;top:28px;width:0;height:0;margin-left:-5px;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:6px solid var(--open);z-index:0;cursor:help}
.aj-pg-key{display:inline-block;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-bottom:5px solid var(--open);margin:0 2px 1px 0}
.aj-pg-over{margin:2px 0 6px;padding:8px 10px;border-radius:10px;background:rgba(208,52,44,.08);color:#b3261e;font-size:13px;font-weight:600;line-height:1.4}
.aj-pg-ph-meta{font-size:12.5px;color:var(--ink3);display:flex;gap:6px;align-items:baseline;flex-wrap:wrap}
.aj-pg-ico{width:26px;height:26px;flex-shrink:0;display:inline-grid;place-items:center;padding:0;border:0;border-radius:50%;background:transparent;color:var(--ink3);cursor:pointer;transition:background .15s,color .15s}
.aj-pg-ico svg{width:16px;height:16px}
.aj-pg-ico:hover{background:#e6e6eb;color:var(--ink)}
.aj-pg-ico:focus-visible{outline:2px solid #0071e3;outline-offset:1px}
.aj-pg-undo{font:inherit;font-size:12.5px;color:var(--ink2);background:none;border:0;padding:0;margin-left:auto;cursor:pointer;text-decoration:underline}
.aj-pg-row{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap}
.aj-pg-back{font:inherit;font-size:14px;color:var(--ink2);background:none;border:0;cursor:pointer;padding:6px 2px}
.aj-pg-back:hover{color:var(--ink)}
.aj-pg-go{font:inherit;font-size:15px;font-weight:600;padding:12px 20px;border-radius:12px;border:0;background:var(--ink);color:#fff;cursor:pointer;transition:transform .15s,box-shadow .15s}
.aj-pg-go:hover{transform:translateY(-1px);box-shadow:0 10px 22px rgba(0,0,0,.18)}
.aj-pg-go:disabled{opacity:.6;cursor:default;transform:none;box-shadow:none}
.aj-pg-done{display:flex;align-items:center;gap:12px;padding:14px 16px;border-radius:16px;background:rgba(31,157,85,.10);color:var(--good);font-size:15px;font-weight:600}
.aj-pg-done svg{width:22px;height:22px;flex:none}
.aj-pg-left{text-align:center;font-size:12.5px;color:rgba(255,255,255,.7);margin-top:12px}
`

const S = {
  warn100: { margin: '8px 0 4px', padding: '12px 14px', borderRadius: 10, background: '#fdecea', border: '1.5px solid #c0392b', color: '#7a1f17' },
  warn100Head: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  warn100Row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: '0.5px solid rgba(192,57,43,0.25)', fontSize: 13 },
  warn100Btn: { fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '4px 11px', borderRadius: 6, border: 0, background: '#c0392b', color: '#fff', cursor: 'pointer', flexShrink: 0 },
  scrim: {
    position: 'fixed', inset: 0, zIndex: 1000000, overflowY: 'auto',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    background: 'rgba(4,21,24,0.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  },
  card: {
    width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 48px)', background: 'rgba(255,255,255,0.97)',
    border: '0.5px solid rgba(255,255,255,0.6)', borderRadius: 16,
    boxShadow: '0 24px 64px rgba(4,21,24,0.5)', padding: 'clamp(28px, 4vw, 44px)',
    display: 'flex', flexDirection: 'column', minHeight: 0,
  },
  eyebrow: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#c0392b', marginBottom: 14 },
  title: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(18px, 2.4vw, 24px)', fontWeight: 300, letterSpacing: '0.03em', color: '#07272D', lineHeight: 1.3, margin: 0 },
  rule: { flexShrink: 0, height: '0.5px', background: 'rgba(7,39,45,0.12)', border: 'none', margin: '20px 0', width: 60 },
  body: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 14, lineHeight: 1.6, color: '#3a5260', margin: '0 0 20px' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, marginRight: -8, paddingRight: 8 },
  group: { flexShrink: 0, marginBottom: 14 },
  groupHeader: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: '#5b8494', margin: '10px 0 6px', paddingTop: 4,
  },
  groupFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 2 },
  groupNote: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#8fa8b3' },
  row: {
    flexShrink: 0, marginBottom: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '12px 14px', borderRadius: 10, border: '0.5px solid rgba(7,39,45,0.12)', background: '#f4f7f8',
  },
  rowMain: { flex: '1 1 auto', minWidth: 0 },
  rowDate: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: '#07272D' },
  rowMeta: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: '#5b8494', marginTop: 3 },
  rowMetaSub: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#8fa8b3', marginTop: 2, cursor: 'help' },
  rowActions: { display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 },
  coreLink: { color: '#7a9aaa', display: 'inline-flex', alignItems: 'center', textDecoration: 'none', padding: '4px 2px' },
  pctInput: {
    width: 56, fontFamily: '"DM Mono", monospace', fontSize: 13, textAlign: 'center', padding: '7px 4px',
    borderRadius: 6, border: '0.5px solid rgba(7,39,45,0.3)', background: '#fff', color: '#07272D',
  },
  pctInputChanged: { border: '1.5px solid #07272D', fontWeight: 600 },
  pctSign: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: '#5b8494' },
  primary: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D',
    border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer',
  },
  snoozeMsg: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#b45309', margin: '14px 0 0', textAlign: 'center' },
  footer: { flexShrink: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 22 },
  linkBtn: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#7a9aaa',
    background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline',
  },
}
